import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { events, getFrameworkVars } from '../../framework/states'
import { stateRef } from '../../framework/store'
import { setValuesInternal, setFiltersNSC } from '../../framework/actions'
import { getRootElement } from '../../framework/loadingMask'
import { updateSession } from '../mainOneappNew/fawkesUtils'
import { ALLOWED_THEME_PREF, APPLY_THEME_EVENT, DARK_THEME, LIGHT_THEME, SYSTEM_THEME, TW_DARK_CLS } from '../../framework/constants'
import type { AuthState, NSCFilters, ThemeValue } from '../../framework/stateTypes'

export { useResetPageTitle, useTsgTitleSync } from '../../framework/title'

export const useThemePreference = (themePref: string | undefined) => {
  useLayoutEffect(() => {
    const vars = getFrameworkVars()
    if (!vars.default_user_theme) {
      return // skip
    }
    const { style, classList } = document.documentElement
    let theme = themePref || vars.default_user_theme
    const applyTheme = (theme: string) => {
      if (theme !== stateRef.current.theme) {
        classList[theme === DARK_THEME ? 'add' : 'remove'](TW_DARK_CLS)
        setValuesInternal({ theme: theme as ThemeValue })
      }
    }
    const rootCls = getRootElement()?.classList
    if (rootCls?.contains('prefers-dark')) {
      rootCls.remove('prefers-dark')
    }
    if (!ALLOWED_THEME_PREF.includes(theme)) {
      theme = vars.default_user_theme
    }
    if (theme === SYSTEM_THEME) {
      const media = window.matchMedia('(prefers-color-scheme: dark)')
      applyTheme(media.matches ? DARK_THEME : LIGHT_THEME)
      style.colorScheme = 'light dark'
      const handler = (e: MediaQueryListEvent) => {
        applyTheme(e.matches ? DARK_THEME : LIGHT_THEME)
      }
      media.addEventListener('change', handler)
      return () => {
        media.removeEventListener('change', handler)
      }
    }
    else {
      applyTheme(theme)
      style.colorScheme = theme
    }
  }, [themePref])

  const currentTheme = stateRef.current.theme
  useEffect(() => {
    if (currentTheme) {
      events.emit(APPLY_THEME_EVENT, currentTheme)
      document.documentElement.dispatchEvent(new CustomEvent(APPLY_THEME_EVENT, { detail: currentTheme }))
    }
  }, [currentTheme])
}

export const useVarConfigEffect = () => {
  useEffect(() => {
    const fontSizeRem = '16px'
    document.documentElement.style.fontSize = fontSizeRem
    document.body.style.fontSize = fontSizeRem
  }, [])
}

export const useRootElementClassNameLayoutEffect = (className: string | null | undefined) => {
  useLayoutEffect(() => {
    const rootEl = getRootElement()
    if (rootEl && className) {
      rootEl.classList.add(className)
    }
    return () => {
      if (rootEl && className) {
        rootEl.classList.remove(className)
      }
    }
  }, [className])
}

export const useTsgTenantInfo = (auth: AuthState | undefined) => {
  const { tsg_id, tsg_name } = auth || {} as Partial<AuthState>
  const rootEl = getRootElement()
  if (!rootEl) {
    return
  }
  if (tsg_id && tsg_name) {
    rootEl.setAttribute('data-tsg-id', tsg_id)
  }
}

export const useFawkesSession = (auth: AuthState | undefined) => {
  const { access_token, access_token_decoded: { exp } = {}, fawkes_tenant_id } = auth || {} as Partial<AuthState>
  useEffect(() => { // if fawkes iam session not used, fawkes_tenant_id won't exist
    if (access_token && fawkes_tenant_id && exp) {
      updateSession({
        sub: fawkes_tenant_id,
        token: access_token,
        exp,
      })
    }
  }, [access_token, exp, fawkes_tenant_id])
}

export const useGlobalEventListener = <T = any>(eventName: string, handler?: ((event: T) => void)) => {
  const savedHandler = useRef(handler)
  savedHandler.current = handler
  useEffect(() => {
    const listner = (event: T) => {
      savedHandler.current?.(event)
    }
    events.on(eventName, listner)
    return () => {
      events.off(eventName, listner)
    }
  }, [eventName])
}

export const useGlobalEventListenerOnce = <T = any>(eventName: string, handler?: ((event: T) => void)) => {
  const savedHandler = useRef(handler)
  savedHandler.current = handler
  const listner = useCallback((event: T) => {
    savedHandler.current?.(event)
  }, [])
  useEffect(() => {
    events.once(eventName, listner)
    return () => {
      events.off(eventName, listner)
    }
  }, [eventName, listner])
}

// COPILOT-768 for co-pilot
export const useFiltersNSC = (filters?: NSCFilters) => {
  useEffect(() => {
    setFiltersNSC(filters)
  }, [filters])
  useEffect(() => { // clear filter when unmount
    return () => {
      setFiltersNSC()
    }
  }, [])
}

export const useThemeOverride = (theme: ThemeValue | undefined) => {
  useLayoutEffect(() => {
    if (theme) {
      setValuesInternal({ themeOverride: theme })
      return () => setValuesInternal({ themeOverride: undefined })
    }
  }, [theme])
}
useThemeOverride.DARK = DARK_THEME
useThemeOverride.LIGHT = LIGHT_THEME

export const useTheme = () => {
  const [, setTheme] = useState()
  useGlobalEventListener(APPLY_THEME_EVENT, setTheme)
  return stateRef.current.theme
}
useTheme.DARK = DARK_THEME
useTheme.LIGHT = LIGHT_THEME
